import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { Trash2, Upload as UploadIcon, Plug, Info, Download, ChevronDown, ChevronUp, ShieldCheck, Sliders, Clock, TrendingUp, TrendingDown, MinusCircle, AlertCircle, CheckCircle2, Circle } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { key: "overview",     label: "Overview" },
  { key: "opportunities", label: "Opportunities" },
  { key: "competitors",  label: "Competitors" },
  { key: "history",      label: "History" },
];

const STARTER_TEMPLATES = [
  { name: "NTD Canada", file: "ntd_canada_starter.csv", desc: "20 popular SKUs prefilled." },
  { name: "Kal Tire Distribution", file: "kal_distribution_starter.csv", desc: "20 SKUs, winter-weighted." },
  { name: "Groupe Touchette", file: "groupe_touchette_starter.csv", desc: "20 SKUs, BFG/Uniroyal family." },
];

const STATUS_META = {
  competitive:       { label: "Competitive",       cls: "bg-[#14624A]/10 text-[#14624A]", Icon: CheckCircle2 },
  premium_position:  { label: "Premium Position",  cls: "bg-[var(--tm-gold)]/15 text-[#8a6318]", Icon: ShieldCheck },
  above_market:      { label: "Above Market",      cls: "bg-[#3B424B]/10 text-[#3B424B]", Icon: TrendingUp },
  below_market:      { label: "Below Market",      cls: "bg-[#C9943D]/15 text-[#8a6318]", Icon: TrendingDown },
  insufficient:      { label: "Insufficient Data", cls: "bg-[#eef0f2] text-[#6b6f77]", Icon: Circle },
};

const ACTION_META = {
  match:  { label: "MATCH — bring price to median" },
  beat:   { label: "BEAT — undercut competitor low" },
  hold:   { label: "HOLD — price is competitive" },
  raise:  { label: "RAISE — margin left on the table" },
  discount: { label: "DISCOUNT — aging inventory" },
  not_recommended: { label: "NOT RECOMMENDED" },
};

const CONFIDENCE_META = {
  high:   { label: "High confidence",   cls: "text-[#14624A]" },
  medium: { label: "Medium confidence", cls: "text-[#8a6318]" },
  low:    { label: "Low confidence",    cls: "text-[#3B424B]" },
  insufficient: { label: "Insufficient", cls: "text-[#6b6f77]" },
};

function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return "just now";
  if (h < 24) return `${Math.round(h)} h ago`;
  return `${Math.round(h / 24)} d ago`;
}
const fmt$ = (v) => (v ? `$${Number(v).toFixed(2)}` : "—");

export default function MarketPage() {
  const [demo, setDemo] = useState(false);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [providers, setProviders] = useState([]);
  const [observations, setObservations] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [expanded, setExpanded] = useState({});

  // Competitors tab CSV upload state (reused from prior iter)
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sourceName, setSourceName] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, opp, pr, obs, srcs, cov] = await Promise.all([
        api.get("/market/overview", { params: { demo } }),
        api.get("/market/opportunities", { params: { demo } }),
        api.get("/market/providers"),
        api.get("/market/observations", { params: { demo } }),
        demo ? Promise.resolve({ data: { sources: [] } }) : api.get("/market-comparables"),
        api.get("/market/coverage", { params: { demo } }),
      ]);
      setOverview(o.data);
      setOpportunities(opp.data.opportunities || []);
      setProviders(pr.data.providers || []);
      setObservations(obs.data.items || []);
      setSources(srcs.data.sources || []);
      setCoverage(cov.data);
    } finally { setLoading(false); }
  }, [demo]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f || !sourceName.trim()) { toast.error("Pick a file and a source label."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("source_name", sourceName.trim());
      const token = localStorage.getItem("tm_token");
      const res = await fetch(`${API}/market-comparables`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Upload failed");
      toast.success(`Loaded ${json.inserted} rows from ${json.source_name}${json.skipped_rows ? ` (${json.skipped_rows} skipped)` : ""}.`);
      setSourceName(""); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const removeSource = async (name) => {
    if (!window.confirm(`Remove all comparables from "${name}"?`)) return;
    try { await api.delete(`/market-comparables/${encodeURIComponent(name)}`); toast.success("Source removed."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const StatusPill = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.insufficient;
    const I = m.Icon;
    return <span className={`tm-badge inline-flex items-center gap-1 ${m.cls}`}><I size={12} /> {m.label}</span>;
  };

  return (
    <DashboardLayout demoBanner={demo} demoMode={demo} onToggleDemo={() => setDemo(!demo)}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" data-testid="market-heading">Market Price Intelligence</h1>
          <p className="mt-1 text-sm text-[#3B424B] max-w-2xl">Know where you stand before you set the price. Recommendations respect your minimum margin — TreadMargin never changes prices automatically.</p>
        </div>
        <Link to="/app/settings/pricing-strategy" className="tm-btn-outline h-10 inline-flex items-center gap-1.5 text-sm" data-testid="market-strategy-link"><Sliders size={14} /> Pricing Strategy</Link>
      </div>

      {/* Tab bar */}
      <div className="mt-6 border-b border-[var(--tm-border)] flex gap-1 overflow-x-auto tm-scroll" role="tablist" data-testid="market-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 h-10 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-[var(--tm-charcoal)] text-[#0F1115]" : "border-transparent text-[#3B424B] hover:text-[#0F1115]"}`}
            data-testid={`market-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {loading ? <div className="mt-6 text-sm text-[#3B424B]">Loading…</div> : (
        <div className="mt-6">
          {/* ==================== OVERVIEW ==================== */}
          {tab === "overview" && (
            <div className="space-y-6" data-testid="market-overview">
              {overview?.has_data ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-4">
                    <div className="tm-card p-5 bg-white">
                      <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Market Position</div>
                      <div className="mt-2 text-4xl font-bold text-[#0F1115]" data-testid="market-position-score">{overview.score}<span className="text-lg text-[#3B424B]">/100</span></div>
                      <div className="mt-1 text-xs text-[#3B424B]">Across {overview.coverage.matched} of {overview.coverage.total_skus} SKUs</div>
                    </div>
                    {[["Competitive", overview.breakdown.competitive || 0, "text-[#14624A]"],
                      ["Above Market", overview.breakdown.above_market || 0, "text-[#3B424B]"],
                      ["Below Market", overview.breakdown.below_market || 0, "text-[#8a6318]"]].map(([lab, val, cls]) => (
                      <div key={lab} className="tm-card p-5 bg-white">
                        <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">{lab}</div>
                        <div className={`mt-2 text-3xl font-bold ${cls}`}>{val}</div>
                        <div className="mt-1 text-xs text-[#3B424B]">SKUs</div>
                      </div>
                    ))}
                  </div>

                  {/* Coverage Nudge — encourage a CSV upload when PMC coverage is thin */}
                  {coverage && coverage.has_data && coverage.total_skus > 0 && coverage.overall_coverage_pct < 20 && (
                    <div className="tm-card p-4 bg-[var(--tm-gold)]/10 border-[#e6d29a] flex items-start gap-3" data-testid="coverage-nudge">
                      <AlertCircle size={18} className="text-[#8a6318] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#0F1115]">Only {coverage.overall_coverage_pct}% of your SKUs have a market comparable.</div>
                        <p className="mt-1 text-xs text-[#3B424B]">To unlock more Opportunities and a more reliable Market Position score, upload an authorized comparables feed (distributor sheet, licensed export, or supplier sample).</p>
                        <button
                          type="button"
                          onClick={() => setTab("competitors")}
                          className="mt-3 tm-btn h-9 inline-flex items-center gap-1.5 text-xs"
                          data-testid="coverage-nudge-upload-btn"
                        >
                          <UploadIcon size={13} /> Upload a comparables CSV
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Match Quality Panel — per-provider coverage of the dealer's SKUs */}
                  {coverage && coverage.has_data && coverage.providers.length > 0 && (
                    <div className="tm-card bg-white overflow-hidden" data-testid="market-coverage-panel">
                      <div className="p-4 border-b border-[var(--tm-border)] flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <div className="font-semibold text-sm">Match Quality</div>
                          <div className="text-xs text-[#3B424B] mt-0.5">How many of your SKUs have a comparable price from each source.</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold font-mono text-[#0F1115]" data-testid="coverage-overall-pct">{coverage.overall_coverage_pct}%</div>
                          <div className="text-xs text-[#3B424B]">{coverage.overall_matched_skus} / {coverage.total_skus} SKUs matched</div>
                        </div>
                      </div>
                      <div className="divide-y divide-[var(--tm-border)]">
                        {coverage.providers.map((p) => (
                          <div key={p.provider} className="px-4 py-3 flex items-center gap-4 text-sm" data-testid={`coverage-row-${p.provider}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-[#0F1115] truncate">{p.label}</div>
                              <div className="text-xs text-[#3B424B]">{p.observations_total} observation{p.observations_total === 1 ? "" : "s"} · {p.matched_skus} SKU{p.matched_skus === 1 ? "" : "s"} matched</div>
                            </div>
                            <div className="w-40 hidden sm:block">
                              <div className="h-2 rounded-full bg-[var(--tm-off)] overflow-hidden">
                                <div className="h-full bg-[var(--tm-charcoal)]" style={{ width: `${Math.min(100, p.coverage_pct)}%` }} />
                              </div>
                            </div>
                            <div className="w-16 text-right font-mono text-sm">{p.coverage_pct}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="tm-card p-4 bg-white text-xs text-[#3B424B]" data-testid="market-overview-strategy">
                    Current strategy: <b className="text-[#0F1115]">{overview.strategy.strategy.replace("_", " ")}</b> · Min margin: <b className="text-[#0F1115]">{overview.strategy.min_margin_pct}%</b> · Location: <b className="text-[#0F1115]">{overview.strategy.location_context}</b>
                  </div>
                  <div className="tm-card bg-white overflow-hidden">
                    <div className="p-4 border-b border-[var(--tm-border)] font-semibold text-sm">All SKUs — Market Position</div>
                    <div className="overflow-x-auto tm-scroll">
                      <table className="w-full text-sm min-w-[820px]">
                        <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]">
                          <tr>
                            <th className="p-3">SKU</th><th className="p-3">Brand / Size</th>
                            <th className="p-3 text-right">Your Price</th><th className="p-3 text-right">Median</th>
                            <th className="p-3 text-right">vs Median</th><th className="p-3">Confidence</th><th className="p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {observations.map((r) => (
                            <tr key={r.sku} className="border-t border-[var(--tm-border)]" data-testid={`market-row-${r.sku}`}>
                              <td className="p-3 font-mono">{r.sku}</td>
                              <td className="p-3">{r.brand} <span className="text-[#3B424B]">·</span> <span className="font-mono">{r.size || r.tire_size}</span></td>
                              <td className="p-3 text-right font-mono">{fmt$(r.dealer_price ?? r.your_price)}</td>
                              <td className="p-3 text-right font-mono">{fmt$(r.market_median)}</td>
                              <td className="p-3 text-right font-mono">{r.position_pct_vs_median ? `${r.position_pct_vs_median > 0 ? "+" : ""}${r.position_pct_vs_median}%` : "—"}</td>
                              <td className={`p-3 text-xs ${CONFIDENCE_META[r.confidence]?.cls}`}>{CONFIDENCE_META[r.confidence]?.label || "—"}</td>
                              <td className="p-3"><StatusPill status={r.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="tm-card p-8 bg-white" data-testid="market-overview-empty">
                  <div className="font-semibold">No inventory or market data yet.</div>
                  <p className="mt-2 text-sm text-[#3B424B]">Upload inventory first, then add an authorized market feed on the Competitors tab. Or enable demo mode to preview.</p>
                </div>
              )}
            </div>
          )}

          {/* ==================== OPPORTUNITIES ==================== */}
          {tab === "opportunities" && (
            <div className="space-y-3" data-testid="market-opportunities">
              {opportunities.length === 0 ? (
                <div className="tm-card p-8 bg-white">
                  <div className="font-semibold">Nothing to change right now.</div>
                  <p className="mt-2 text-sm text-[#3B424B]">Either your prices are already competitive or there aren't enough comparables. Upload a feed on the Competitors tab.</p>
                </div>
              ) : opportunities.map((o) => {
                const rec = o.recommendation || {};
                return (
                  <div key={o.sku} className="tm-card bg-white overflow-hidden" data-testid={`opp-${o.sku}`}>
                    <button onClick={() => setExpanded((e) => ({ ...e, [o.sku]: !e[o.sku] }))} className="w-full p-5 flex items-start justify-between gap-4 text-left hover:bg-[var(--tm-off)]/40 transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="tm-badge bg-[var(--tm-charcoal)] text-white">{ACTION_META[rec.action]?.label || rec.action}</span>
                          <StatusPill status={o.status} />
                          <span className={`text-xs ${CONFIDENCE_META[o.confidence]?.cls}`}>{CONFIDENCE_META[o.confidence]?.label}</span>
                          <span className="text-xs text-[#3B424B] inline-flex items-center gap-1"><Clock size={11} /> {fmtHours(o.freshest_hours)}</span>
                        </div>
                        <div className="mt-2 font-semibold text-[#0F1115]">{o.brand} {o.model || ""} <span className="font-mono text-[#3B424B]">{o.size}</span> <span className="text-xs text-[#3B424B]">· SKU {o.sku}</span></div>
                        <div className="mt-1 text-sm text-[#3B424B]">{rec.reason}</div>
                        <div className="mt-3 flex gap-6 text-sm">
                          <div><span className="text-[#3B424B] text-xs">Your price</span><div className="font-mono">{fmt$(o.dealer_price)}</div></div>
                          <div><span className="text-[#3B424B] text-xs">Median</span><div className="font-mono">{fmt$(o.market_median)}</div></div>
                          <div><span className="text-[#3B424B] text-xs">Low – High</span><div className="font-mono">{fmt$(o.market_low)} – {fmt$(o.market_high)}</div></div>
                          {rec.suggested_price ? <div><span className="text-[#3B424B] text-xs">Suggested</span><div className="font-mono text-[var(--tm-forest)] font-semibold">{fmt$(rec.suggested_price)}</div></div> : null}
                          <div><span className="text-[#3B424B] text-xs">On-hand</span><div className="font-mono">{o.quantity_on_hand}</div></div>
                        </div>
                      </div>
                      {expanded[o.sku] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expanded[o.sku] && (
                      <div className="border-t border-[var(--tm-border)] p-5 bg-[var(--tm-off)]/40">
                        <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-2">Comparable observations ({o.sample_size})</div>
                        <div className="overflow-x-auto tm-scroll">
                          <table className="w-full text-xs min-w-[640px]">
                            <thead className="text-[#3B424B]"><tr><th className="text-left p-2">Retailer</th><th className="text-left p-2">Price</th><th className="text-left p-2">Conditions</th><th className="text-left p-2">Match</th><th className="text-left p-2">Checked</th><th className="text-left p-2">Source</th></tr></thead>
                            <tbody>
                              {(o.observations || []).map((ob, i) => (
                                <tr key={i} className="border-t border-[var(--tm-border)]">
                                  <td className="p-2">{ob.retailer}</td>
                                  <td className="p-2 font-mono">{fmt$(ob.price)}{ob.basis === "set_of_four" ? " /4" : ""}</td>
                                  <td className="p-2 text-[#3B424B]">{[ob.stock, ob.membership_required && "membership req'd", ob.installation_included && "install incl.", ob.shipping].filter(Boolean).join(" · ") || "—"}</td>
                                  <td className="p-2 uppercase text-[10px] tracking-wider">{ob._tier}</td>
                                  <td className="p-2 text-[#3B424B]">{ob.observed_at ? fmtHours((Date.now() - new Date(ob.observed_at)) / 3600000) : "—"}</td>
                                  <td className="p-2">{ob.source_url ? <a href={ob.source_url} target="_blank" rel="noreferrer" className="underline text-[var(--tm-forest)]">link</a> : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ==================== COMPETITORS ==================== */}
          {tab === "competitors" && (
            <div className="space-y-6" data-testid="market-competitors">
              <div className="tm-card p-5 bg-white">
                <div className="flex items-start gap-2">
                  <Plug size={16} className="mt-0.5 text-[var(--tm-forest)]" />
                  <div className="flex-1">
                    <div className="font-semibold">Data source status</div>
                    <p className="mt-1 text-xs text-[#3B424B]">TreadMargin only uses observations you have the right to store. Retailers that don't offer a legitimate feed (Costco, Canadian Tire, Kal Tire, etc.) are shown honestly as Unavailable — we never fabricate their prices.</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="market-providers-list">
                  {providers.map((p) => (
                    <div key={p.name} className="tm-card p-3 bg-[var(--tm-off)]" data-testid={`market-provider-${p.name}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-sm truncate">{p.label}</div>
                        <span className={`tm-badge text-[10px] ${p.status === "live" ? "bg-[#14624A]/10 text-[#14624A]" : p.status === "unavailable" ? "bg-[#eef0f2] text-[#6b6f77]" : "bg-[var(--tm-gold)]/15 text-[#8a6318]"}`}>
                          {p.status === "live" ? "LIVE" : p.status === "unavailable" ? "UNAVAILABLE" : "PENDING"}
                        </span>
                      </div>
                      <div className="mt-1 text-[11px] text-[#3B424B]">Method: {p.method}</div>
                      {p.reason && <div className="mt-1 text-[11px] text-[#3B424B]">{p.reason}</div>}
                      {p.records_last_run > 0 && <div className="mt-1 text-[11px] text-[#3B424B]">Rows: {p.records_last_run.toLocaleString()}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* CSV upload panel — unchanged from previous iteration */}
              <div className="tm-card p-5 bg-white" data-testid="market-feed-panel">
                <div className="font-semibold">Upload an authorized market feed</div>
                <p className="mt-1 text-xs text-[#3B424B]">Required columns: <b>brand</b>, <b>tire_size</b>, <b>competitor_price</b>. Optional: <b>competitor</b>, <b>url</b>. Use a distributor sheet you have rights to redistribute.</p>
                <form onSubmit={onUpload} className="mt-4 grid gap-3 sm:grid-cols-[1fr,1fr,auto] items-end">
                  <div>
                    <label className="tm-label">Source label</label>
                    <input className="tm-input" placeholder="e.g. NTD Weekly Sheet — Feb 25" value={sourceName} onChange={(e) => setSourceName(e.target.value)} data-testid="market-source-name" />
                  </div>
                  <div>
                    <label className="tm-label">CSV / XLSX file</label>
                    <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="tm-input" data-testid="market-source-file" />
                  </div>
                  <button type="submit" className="tm-btn-primary h-11 px-4 inline-flex items-center gap-2" disabled={busy} data-testid="market-source-upload"><UploadIcon size={14} /> {busy ? "Uploading…" : "Upload feed"}</button>
                </form>
                {sources.length > 0 && (
                  <div className="mt-5 border-t border-[var(--tm-border)] pt-4">
                    <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-2">Connected sources</div>
                    <div className="space-y-2">
                      {sources.map((s) => (
                        <div key={s.source_name} className="flex items-center justify-between gap-3 tm-card p-3 bg-[var(--tm-off)]" data-testid={`market-source-${s.source_name}`}>
                          <div className="min-w-0"><div className="font-semibold truncate">{s.source_name}</div><div className="text-xs text-[#3B424B]">{s.row_count.toLocaleString()} rows · {s.sample_brands.slice(0, 4).join(", ")}</div></div>
                          <button onClick={() => removeSource(s.source_name)} className="tm-btn-outline h-9 px-3 inline-flex items-center gap-1.5 text-xs" data-testid={`market-source-delete-${s.source_name}`}><Trash2 size={13} /> Remove</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-5 border-t border-[var(--tm-border)] pt-4" data-testid="market-templates">
                  <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-2">Starter templates</div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {STARTER_TEMPLATES.map((t) => (
                      <a key={t.file} href={`/templates/${t.file}`} download className="tm-card p-3 bg-[var(--tm-off)] hover:bg-white transition-colors flex flex-col gap-1.5" data-testid={`market-template-${t.file}`}>
                        <div className="flex items-center gap-2"><Download size={14} className="text-[var(--tm-forest)]" /><div className="font-semibold text-sm">{t.name}</div></div>
                        <div className="text-[11px] text-[#3B424B] leading-snug">{t.desc}</div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================== HISTORY ==================== */}
          {tab === "history" && (
            <div className="tm-card p-8 bg-white" data-testid="market-history">
              <div className="font-semibold inline-flex items-center gap-2"><Info size={14} /> Price history storage is on.</div>
              <p className="mt-2 text-sm text-[#3B424B]">Every observation is stored with a timestamp — nothing is ever overwritten. As soon as we have a second observation for the same SKU, this view will show 7 / 30 / 90-day price movement per retailer.</p>
              <p className="mt-2 text-xs text-[#3B424B]">Charts ship in a follow-up phase; the underlying data collection is already live.</p>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
